import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ImportFlowState,
  ActualServer,
  ImportResult,
  API_BASE_URL,
  OverspendSuggestionResult
} from '../types';
import { auth } from '../utils/firebase';
import { suggestOverspendCoverage, isRateLimitError } from '../utils/overspendApi';

/**
 * Get the current user's ID token for API authentication
 */
async function getIdToken(): Promise<string | null> {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    return await user.getIdToken();
  } catch (error) {
    console.error('Failed to get ID token:', error);
    return null;
  }
}

export const useImportFlow = () => {
  const { t } = useTranslation();
  const [importFlow, setImportFlow] = useState<ImportFlowState>({
    step: 'servers',
    budgets: [],
    accounts: [],
    hasClickedImport: false,
    isRefreshingAccounts: false,
    accountPreselected: false
  });
  const [showImportModal, setShowImportModal] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [showOverspendModal, setShowOverspendModal] = useState(false);
  const [overspendSuggestion, setOverspendSuggestion] = useState<OverspendSuggestionResult | null>(null);
  const [overspendLoading, setOverspendLoading] = useState(false);
  const [overspendError, setOverspendError] = useState<string | null>(null);

  const resetOverspendState = () => {
    setShowOverspendModal(false);
    setOverspendSuggestion(null);
    setOverspendLoading(false);
    setOverspendError(null);
  };

  // Reset import flow state
  const resetImportFlow = () => {
    setImportFlow({
      step: 'servers',
      budgets: [],
      accounts: [],
      hasClickedImport: false,
      isRefreshingAccounts: false,
      accountPreselected: false
    });
    setImportResult(null);
    resetOverspendState();
  };

  const loadOverspendSuggestions = async (
    server: ActualServer,
    budgetId: string,
    budgetPassword?: string
  ) => {
    setOverspendLoading(true);
    setOverspendError(null);
    setOverspendSuggestion(null);

    try {
      const suggestion = await suggestOverspendCoverage({
        server,
        budgetId,
        budgetPassword
      });

      if (suggestion.overspentCategories.length > 0) {
        setOverspendSuggestion(suggestion);
        setShowOverspendModal(true);
      }
    } catch (error) {
      console.warn('Failed to load overspend suggestions:', error);
      setOverspendError(
        isRateLimitError(error)
          ? t('rateLimitExceeded', { ns: 'overspend' })
          : error instanceof Error ? error.message : t('errorGeneric', { ns: 'overspend' })
      );
    } finally {
      setOverspendLoading(false);
    }
  };

  // Start import flow
  const startImportFlow = (
    activeServer: ActualServer | null,
    loadAccountsForImport: (
      server: ActualServer,
      budgetId: string,
      budgetPassword?: string,
      preferredAccountId?: string | null
    ) => void,
    preferredAccountId?: string | null
  ) => {
    resetImportFlow();
    setShowImportModal(true);

    if (activeServer && activeServer.defaultBudgetId) {
      setImportFlow(prev => ({
        ...prev,
        selectedServer: activeServer,
        selectedBudgetId: activeServer.defaultBudgetId,
        budgetPassword: activeServer.defaultBudgetPassword,
        step: 'accounts'
      }));

      loadAccountsForImport(
        activeServer,
        activeServer.defaultBudgetId,
        activeServer.defaultBudgetPassword,
        preferredAccountId
      );
    } else if (activeServer) {
      setImportFlow(prev => ({
        ...prev,
        selectedServer: activeServer,
        step: 'no-default-budget'
      }));
    }
  };

  // Update import flow state
  const updateImportFlow = (changes: Partial<ImportFlowState>) => {
    setImportFlow(prev => ({ ...prev, ...changes }));
  };

  // Execute the actual import
  const executeImport = async (csvContent: string, setError: (error: string | null) => void, saveLastUsedConfig: (serverId: string, budgetId: string, accountId: string, budgetPassword?: string) => void) => {
    if (!importFlow.selectedServer || !importFlow.selectedBudgetId || !importFlow.selectedAccountId) {
      setError(t('missingImportConfiguration', { ns: 'import' }));
      return;
    }

    setIsImporting(true);
    setImportResult(null);
    setError(null);
    resetOverspendState();

    try {
      // Get authentication token
      const token = await getIdToken();
      if (!token) {
        throw new Error(t('authRequiredForImport', { ns: 'auth' }));
      }

      const requestBody: any = {
        csvContent: csvContent,
        serverURL: importFlow.selectedServer.url,
        password: importFlow.selectedServer.password,
        budgetId: importFlow.selectedBudgetId,
        accountId: importFlow.selectedAccountId
      };

      if (importFlow.budgetPassword) {
        requestBody.budgetPassword = importFlow.budgetPassword;
      }

      let response;
      try {
        response = await fetch(`${API_BASE_URL}/import-transactions`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestBody),
        });
      } catch (networkError) {
        throw new Error(`${t('networkError', { ns: 'import' })}: ${networkError instanceof Error ? networkError.message : t('failedToConnect', { ns: 'import' })}`);
      }

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
        throw new Error(errorData.error || t('failedToImport', { ns: 'import' }));
      }

      const importResult = await response.json();
      
      setImportResult({
        imported: importResult.imported,
        errors: importResult.errors || []
      });

      // Save the successful configuration
      if (importFlow.selectedServer) {
        saveLastUsedConfig(
          importFlow.selectedServer.id,
          importFlow.selectedBudgetId,
          importFlow.selectedAccountId,
          importFlow.budgetPassword
        );
      }

      if (importResult.errors && importResult.errors.length > 0) {
        console.warn('Import completed with warnings:', importResult.errors);
      }

      if (importResult.imported > 0 && importFlow.selectedServer && importFlow.selectedBudgetId) {
        await loadOverspendSuggestions(
          importFlow.selectedServer,
          importFlow.selectedBudgetId,
          importFlow.budgetPassword
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToImportToActual', { ns: 'import' }));
    } finally {
      setIsImporting(false);
    }
  };

  const openOverspendModal = () => {
    if (importFlow.selectedServer && importFlow.selectedBudgetId) {
      setShowOverspendModal(true);
      if (!overspendSuggestion && !overspendLoading) {
        loadOverspendSuggestions(
          importFlow.selectedServer,
          importFlow.selectedBudgetId,
          importFlow.budgetPassword
        );
      }
    }
  };

  // Close import modal and reset clicked flag
  const closeImportModal = () => {
    setShowImportModal(false);
    updateImportFlow({ hasClickedImport: false, isRefreshingAccounts: false });
  };

  const closeOverspendModal = () => {
    setShowOverspendModal(false);
  };

  const updateOverspendSuggestion = (suggestion: OverspendSuggestionResult) => {
    setOverspendSuggestion(suggestion);
  };

  return {
    importFlow,
    showImportModal,
    isImporting,
    importResult,
    showOverspendModal,
    overspendSuggestion,
    overspendLoading,
    overspendError,
    resetImportFlow,
    startImportFlow,
    updateImportFlow,
    executeImport,
    setShowImportModal,
    closeImportModal,
    closeOverspendModal,
    openOverspendModal,
    updateOverspendSuggestion
  };
};
